import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DayKind } from '@domain/calendar.ts'
import { childPinKey } from '@application/ports.ts'
import type { Actor, DomainEvent } from '@domain/events.ts'
import type {
  CommandId,
  MemberId,
  PhotoId,
  PurchaseId,
  RewardId,
  StepId,
  TaskId,
} from '@domain/ids.ts'
import type { Adaptations, Step } from '@domain/events.ts'
import type { Purchase, Reward, Task } from '@domain/state.ts'
import { adultsOf, childrenOf, memberDayOf, taskDayOf, tasksOf } from '@domain/state.ts'
import { intentoDe, type Intento } from '@domain/rules/mates.ts'
import {
  addAdult,
  addChild,
  archiveMember,
  completeStep,
  declareDayType,
  declareDayRange,
  deliverPurchase,
  grantPurchase,
  markTaskDone,
  openChest,
  pauseDay,
  purchase,
  cancelPurchase,
  returnTask,
  archiveTask,
  archiveReward,
  defineReward,
  defineTask,
  renameTask,
  repriceReward,
  retuneTask,
  updateLook,
  finishPractice,
  restoreMember,
  restoreTask,
  revokeValidation,
  setReferencePhoto,
  setReminders,
  updateSteps,
  startTimer,
  uncompleteStep,
  updateAdaptations,
  validateTask,
} from '@domain/commands.ts'
import { Juegos } from '../child/Juegos.tsx'
import { Mates } from '../child/Mates.tsx'
import { Shop } from '../child/Shop.tsx'
import { TaskScreen } from '../child/TaskScreen.tsx'
import { TimerScreen } from '../child/TimerScreen.tsx'
import { OneThing } from '../child/OneThing.tsx'
import { Today, momentFor } from '../child/Today.tsx'
import { Pendiente, PendienteDeOtro } from '../child/Pendiente.tsx'
import { overdueRoutines, type Reminder } from '@domain/rules/pending.ts'
import { ChildPinGate } from '../child/ChildPinGate.tsx'
import { WhoIsIt } from '../child/WhoIsIt.tsx'
import { Assistant } from '../parent/Assistant.tsx'
import { Calendario } from '../parent/Calendario.tsx'
import { ChildConfig } from '../parent/ChildConfig.tsx'
import { ShopEditor } from '../parent/ShopEditor.tsx'
import { Insights } from '../parent/Insights.tsx'
import { nextMeetingDay } from '@domain/plan.ts'
import { missionLadder, routineLadder } from '@domain/seed.ts'
import { ParentMode } from '../parent/ParentMode.tsx'
import { PorQue } from '../parent/PorQue.tsx'
import { QuienAdulto } from '../parent/QuienAdulto.tsx'
import { SemaforoSetup } from '../parent/SemaforoSetup.tsx'
import { Semaforo } from '../child/Semaforo.tsx'
import { SystemScreen } from '../shell/SystemScreen.tsx'
import { es } from '../strings/es.ts'
import { usePwaUpdate } from '../pwa.ts'
import { PinGate } from './PinGate.tsx'
import { INACTIVIDAD_PADRES_MS, useInactividad } from './useInactividad.ts'
import { Setup } from './Setup.tsx'
import { assistantAvailable } from '@adapters/ai/config.ts'
import { messageFor, useHome } from './useHome.ts'

type View =
  | { name: 'who' }
  | { name: 'childPin'; memberId: MemberId }
  | { name: 'today'; memberId: MemberId }
  | { name: 'task'; memberId: MemberId; taskId: TaskId }
  | { name: 'shop'; memberId: MemberId }
  | { name: 'timer'; memberId: MemberId }
  | { name: 'juegos'; memberId: MemberId }
  | { name: 'mates'; memberId: MemberId }
  | { name: 'pin' }
  | { name: 'quienAdulto' }
  | { name: 'parent' }
  | { name: 'insights' }
  | { name: 'config' }
  | { name: 'shopEditor' }
  | { name: 'porQue' }
  | { name: 'calendario' }
  | { name: 'semaforoSetup' }
  | { name: 'semaforo'; minutes: number; limit: number }
  | { name: 'assistant' }
  | { name: 'system' }

const PARENT_ACTOR = (id: MemberId): Actor => ({ kind: 'parent', id })

/**
 * Las pantallas que están detrás del PIN. Es lo que el bloqueo por inactividad
 * vigila, y por eso se escribe como lista y no como «todo lo que no es de un
 * niño»: una pantalla nueva de adulto que no esté aquí se queda abierta, y eso
 * se nota al revisar esta lista y no al cabo de un mes.
 *
 * `pin` no está, porque ahí todavía no se ha entrado. `semaforo` tampoco: lo
 * arranca un adulto, pero lo miran los niños durante la tarde.
 */
const VISTAS_DE_ADULTO: ReadonlySet<View['name']> = new Set<View['name']>([
  'quienAdulto',
  'parent',
  'insights',
  'config',
  'shopEditor',
  'porQue',
  'calendario',
  'semaforoSetup',
  'assistant',
  'system',
])

/** Uno solo, para que «todavía no hay sesión» no sea una lista nueva cada vez. */
const SIN_EVENTOS: readonly DomainEvent[] = []

export function App() {
  const home = useHome()
  const update = usePwaUpdate()
  const [view, setView] = useState<View>({ name: 'who' })
  const [moment, setMoment] = useState(() => momentFor(new Date().getHours()))
  const [minutesNow, setMinutesNow] = useState(() => {
    const ahora = new Date()
    return ahora.getHours() * 60 + ahora.getMinutes()
  })
  const [seeAll, setSeeAll] = useState(false)
  const [adultoActual, setAdultoActual] = useState<MemberId | null>(null)

  // La tablet en la encimera y un adulto que se fue a mitad de un ajuste: el
  // modo padres se cierra solo, igual que si hubiera pulsado «Salir».
  const cerrarModoPadres = useCallback((): void => {
    setAdultoActual(null)
    setView({ name: 'who' })
  }, [])
  useInactividad(VISTAS_DE_ADULTO.has(view.name), INACTIVIDAD_PADRES_MS, cerrarModoPadres)

  // The day moves on while the tablet sits on the counter, so the routine that
  // is "now" is recomputed rather than fixed when the app opened.
  useEffect(() => {
    const id = setInterval(() => {
      const ahora = new Date()
      setMoment(momentFor(ahora.getHours()))
      setMinutesNow(ahora.getHours() * 60 + ahora.getMinutes())
    }, 60_000)
    return () => {
      clearInterval(id)
    }
  }, [])

  const { state, today, run, photos, vault, session, store } = home

  // El registro entero. Cambia de identidad cuando se recarga y solo entonces, así
  // que es la dependencia buena de lo que se calcula a partir de él: contar
  // eventos no lo es, porque nada garantiza que el número cambie cuando cambia la
  // lista.
  const log = session?.events() ?? SIN_EVENTOS

  /**
   * Lo que cada niño ha contestado en el juego de mates.
   *
   * No está en la proyección a propósito: el estado de la casa es lo que hace
   * falta para decidir qué se puede hacer ahora, y esto es material de estudio
   * que solo mira una pantalla.
   */
  const repasos = useMemo(() => {
    const por = new Map<MemberId, Intento[]>()
    for (const event of log) {
      if (event.type !== 'PracticeFinished') continue
      const lista = por.get(event.payload.memberId) ?? []
      lista.push(...event.payload.items.map(intentoDe))
      por.set(event.payload.memberId, lista)
    }
    return por
  }, [log])

  // Which children have their own number. Read once from the vault, which lives
  // outside the event log because a PIN hash is a credential and the log gets
  // exported. Re-read whenever the family changes, so setting one in the parent
  // settings takes effect without closing the app.
  const [guarded, setGuarded] = useState<ReadonlySet<MemberId>>(new Set())
  // The vault is not the event log, so nothing notifies when a PIN is set or
  // cleared. This is the nudge that says "look again".
  const [pinsChanged, setPinsChanged] = useState(0)
  const memberIds = childrenOf(state)
    .map((m) => m.id)
    .join(',')

  useEffect(() => {
    if (!vault) return
    let cancelled = false
    void (async () => {
      const ids = memberIds.length === 0 ? [] : (memberIds.split(',') as MemberId[])
      const withPin = new Set<MemberId>()
      for (const id of ids) {
        // Si el baúl no se puede leer, este niño entra sin número. Falla
        // abierto a propósito: el número existe para que un hermano no haga el
        // gamberro, y ningún fallo de almacenamiento debe dejar a un niño fuera
        // de su propia rutina sin manera de entrar.
        const state = await vault.state(childPinKey(id)).catch(() => null)
        if (state?.isSet) withPin.add(id)
      }
      if (!cancelled) setGuarded(withPin)
    })()
    return () => {
      cancelled = true
    }
  }, [vault, memberIds, pinsChanged])

  /**
   * El adulto que tiene la tablet ahora mismo, elegido al abrir el modo padres.
   *
   * Antes esto era «el primer adulto que se dio de alta», y con dos adultos en
   * casa eso convertía el registro en una historia falsa: «papá validó» el día que
   * estaba trabajando fuera. Con un solo adulto no hay nada que preguntar y se
   * usa ese.
   */
  const adultos = adultsOf(state)
  // Se comprueba que siga estando: un adulto puede quitarse a sí mismo de las
  // listas, y entonces lo que firmara después iría a nombre de alguien archivado.
  const parentId = adultos.find((a) => a.id === adultoActual)?.id ?? adultos[0]?.id
  const childActor = useCallback((id: MemberId): Actor => ({ kind: 'child', id }), [])

  const report = useCallback(
    async (
      result: Promise<{
        ok: boolean
        error?: { code: string; detail?: Record<string, string | number> }
      }>,
    ): Promise<string | null> => {
      const done = await result
      if (done.ok) return null
      return messageFor((done.error?.code ?? 'invalid') as never, done.error?.detail)
    },
    [],
  )

  if (home.failed) {
    return (
      <div className="mx-auto max-w-lg px-5 pt-16" role="alert">
        <p className="text-coral font-bold">{es.shell.loadFailed}</p>
        <p className="text-muted text-sm">{es.shell.loadFailedHint}</p>
      </div>
    )
  }

  if (!home.ready || !session) {
    return <p className="text-muted px-5 pt-16">{es.shell.checking}</p>
  }

  if (state.family === undefined) {
    return (
      <Setup
        session={session}
        store={store}
        onDone={() => {
          setView({ name: 'who' })
        }}
      />
    )
  }

  const children = childrenOf(state)
  const member = 'memberId' in view ? state.members.get(view.memberId) : undefined

  /** Lo que ya tocaba y sigue sin hacer, para un niño. */
  const pendienteDe = (id: MemberId): readonly Task[] => {
    const quien = state.members.get(id)
    if (!quien) return []
    return overdueRoutines(state, id, today as never, minutesNow, quien.reminders)
  }

  const banner = update.ready ? (
    <div role="alert" className="bg-gold-soft border-gold border-b-2 p-3 text-center">
      <span className="font-bold">{es.update.available}</span>{' '}
      <button
        type="button"
        className="text-teal min-h-0 font-bold underline"
        onClick={update.apply}
      >
        {es.update.apply}
      </button>
    </div>
  ) : null

  const body = (): React.ReactNode => {
    switch (view.name) {
      case 'who': {
        const conPendiente = children
          .filter((child) => pendienteDe(child.id).length > 0)
          .map((child) => ({ id: child.id, name: child.name, accent: child.accent }))
        return (
          <>
            <PendienteDeOtro
              nombres={conPendiente}
              onPick={(id) => {
                const memberId = id as MemberId
                setView(
                  guarded.has(memberId)
                    ? { name: 'childPin', memberId }
                    : { name: 'today', memberId },
                )
              }}
            />
            <WhoIsIt
              children={children}
              onPick={(id) => {
                // A child with their own number gets asked for it; one without
                // walks straight in, which is how it was for everyone before.
                setView(
                  guarded.has(id)
                    ? { name: 'childPin', memberId: id }
                    : { name: 'today', memberId: id },
                )
              }}
              onParents={() => {
                setView({ name: 'pin' })
              }}
            />
          </>
        )
      }

      case 'childPin': {
        if (!member || !vault) return null
        return (
          <ChildPinGate
            member={member}
            vault={vault}
            onOpen={() => {
              setView({ name: 'today', memberId: member.id })
            }}
            onCancel={() => {
              setView({ name: 'who' })
            }}
          />
        )
      }

      case 'today': {
        if (!member) return null
        const blocked = openChest(
          state,
          { memberId: member.id },
          session.context(childActor(member.id)),
        )
        const rawBlocked = blocked.ok ? null : messageFor(blocked.error.code)
        const chestBlocked = memberDayOf(state, today as never, member.id).chestOpened
          ? null
          : rawBlocked
        const openTask = (task: Task): void => {
          setView({ name: 'task', memberId: member.id, taskId: task.id })
        }
        const openChestNow = (): void => {
          void run((s, c) => openChest(s, { memberId: member.id }, c), childActor(member.id))
        }
        const leave = (): void => {
          setSeeAll(false)
          setView({ name: 'who' })
        }

        // One child needs the whole day in front of him; the other needs exactly
        // one thing and nothing else. Same data, two ways in.
        const pendiente = (
          <Pendiente tasks={pendienteDe(member.id)} accent={member.accent} onOpen={openTask} />
        )

        if ((member.adaptations.oneThingAtATime ?? false) && !seeAll) {
          return (
            <>
              {pendiente}
              <OneThing
                member={member}
                state={state}
                day={today}
                chestBlocked={chestBlocked}
                onOpenTask={openTask}
                onOpenChest={openChestNow}
                onSeeAll={() => {
                  setSeeAll(true)
                }}
                onShop={() => {
                  setView({ name: 'shop', memberId: member.id })
                }}
                onTimer={() => {
                  setView({ name: 'timer', memberId: member.id })
                }}
                onJuegos={() => {
                  setView({ name: 'juegos', memberId: member.id })
                }}
                onBack={leave}
              />
            </>
          )
        }

        return (
          <>
            {pendiente}
            <Today
              member={member}
              state={state}
              day={today}
              moment={moment}
              chestBlocked={chestBlocked}
              onOpenTask={openTask}
              onOpenChest={openChestNow}
              onShop={() => {
                setView({ name: 'shop', memberId: member.id })
              }}
              onTimer={() => {
                setView({ name: 'timer', memberId: member.id })
              }}
              onJuegos={() => {
                setView({ name: 'juegos', memberId: member.id })
              }}
              onBack={() => {
                setSeeAll(false)
                setView({ name: 'who' })
              }}
            />
          </>
        )
      }

      case 'juegos': {
        if (!member) return null
        return (
          <Juegos
            member={member}
            state={state}
            day={today}
            onMates={() => {
              setView({ name: 'mates', memberId: member.id })
            }}
            onBack={() => {
              setView({ name: 'today', memberId: member.id })
            }}
          />
        )
      }

      case 'mates': {
        // Sin sesión no hay dado, y el dado de esta app es uno solo: el que
        // acaba escrito en el registro. Antes que sacar aquí otro azar de la
        // manga, no se abre el juego.
        if (!member || !session) return null
        const mision = tasksOf(state, member.id, 'mission').find((task) => task.game === 'mates')
        const yaEstaba =
          mision !== undefined &&
          taskDayOf(state, today as never, member.id, mision.id).status === 'validated'
        // Cuántas sesiones lleva hoy. Hace único el identificador del mando sin
        // recurrir al azar, así que pulsar dos veces «terminar» no cuenta dos.
        const sesionesHoy = log.filter(
          (event) =>
            event.type === 'PracticeFinished' &&
            event.payload.memberId === member.id &&
            event.day === today,
        ).length

        return (
          <Mates
            member={member}
            historial={repasos.get(member.id) ?? []}
            mision={
              mision === undefined
                ? undefined
                : {
                    coins: mision.rewards[mision.rung].coins,
                    palabra: member.coinWord ?? es.today.coins,
                    yaEstaba,
                  }
            }
            draw={() => session.context(childActor(member.id)).draw()}
            onFinish={(items) => {
              void run(
                (s, c) =>
                  finishPractice(
                    s,
                    {
                      memberId: member.id,
                      game: 'mates',
                      items,
                      commandId: `mates:${member.id}:${today}:${sesionesHoy}` as CommandId,
                    },
                    c,
                  ),
                childActor(member.id),
              )
            }}
            onBack={() => {
              setView({ name: 'juegos', memberId: member.id })
            }}
          />
        )
      }

      case 'task': {
        const task = state.tasks.get(view.taskId)
        if (!member || !task) return null
        return (
          <TaskScreen
            task={task}
            state={state}
            day={today}
            photos={photos}
            accent={member.accent}
            coinWord={member.coinWord ?? es.today.coins}
            warnOnChange={member.adaptations.literalLanguage}
            oneStepAtATime={member.adaptations.oneThingAtATime ?? false}
            onCompleteStep={(stepId: StepId) => {
              void run(
                (s, c) => completeStep(s, { taskId: task.id, stepId }, c),
                childActor(member.id),
              )
            }}
            onUncompleteStep={(stepId: StepId) => {
              void run(
                (s, c) => uncompleteStep(s, { taskId: task.id, stepId }, c),
                childActor(member.id),
              )
            }}
            onMarkDone={(photoId?: PhotoId) =>
              report(
                run(
                  (s, c) =>
                    markTaskDone(
                      s,
                      {
                        taskId: task.id,
                        // The key includes the attempt, so a task that came
                        // back down can be marked again. Keyed only by day, the
                        // second press was silently swallowed as a duplicate.
                        commandId:
                          `${task.id}:${today}:mark:${taskDayOf(s, c.day, task.memberId, task.id).attempt}` as never,
                        ...(photoId !== undefined ? { photoId } : {}),
                      },
                      c,
                    ),
                  childActor(member.id),
                ),
              )
            }
            onBack={() => {
              setView({ name: 'today', memberId: member.id })
            }}
          />
        )
      }

      case 'shop':
        if (!member) return null
        return (
          <Shop
            member={member}
            state={state}
            day={today}
            onBuy={(reward: Reward) =>
              report(
                run(
                  (s, c) =>
                    purchase(
                      s,
                      {
                        purchaseId:
                          `${member.id}:${today}:${reward.id}:${s.purchases.size}` as never,
                        memberId: member.id,
                        rewardId: reward.id,
                        commandId:
                          `${member.id}:${today}:${reward.id}:${s.purchases.size}` as never,
                      },
                      c,
                    ),
                  childActor(member.id),
                ),
              )
            }
            onUndo={(bought: Purchase) =>
              report(
                run(
                  (s, c) => cancelPurchase(s, { purchaseId: bought.id }, c),
                  childActor(member.id),
                ),
              )
            }
            onBack={() => {
              setView({ name: 'today', memberId: member.id })
            }}
          />
        )

      case 'timer':
        if (!member) return null
        return (
          <TimerScreen
            member={member}
            day={memberDayOf(state, today as never, member.id)}
            accent={member.accent}
            onStart={(minutes) => {
              void run(
                (s, c) => startTimer(s, { memberId: member.id, minutes, source: 'chest' }, c),
                childActor(member.id),
              )
            }}
            onBack={() => {
              setView({ name: 'today', memberId: member.id })
            }}
          />
        )

      case 'pin':
        if (!vault) return null
        return (
          <PinGate
            vault={vault}
            onOpen={() => {
              // Con un solo adulto no hay nada que preguntar. El PIN es de la
              // casa —una sola cosa que recordar— y esto de después es una firma.
              const solo = adultos.length === 1 ? adultos[0] : undefined
              if (solo) {
                setAdultoActual(solo.id)
                setView({ name: 'parent' })
                return
              }
              setView({ name: 'quienAdulto' })
            }}
            onCancel={() => {
              setView({ name: 'who' })
            }}
          />
        )

      case 'quienAdulto':
        return (
          <QuienAdulto
            adultos={adultos}
            onElegir={(memberId) => {
              setAdultoActual(memberId)
              setView({ name: 'parent' })
            }}
            onCancel={() => {
              setView({ name: 'who' })
            }}
          />
        )

      case 'parent': {
        if (!parentId) return null
        const actor = PARENT_ACTOR(parentId)
        return (
          <ParentMode
            state={state}
            events={session.events()}
            day={today}
            photos={photos}
            onApprove={(taskId: TaskId) =>
              report(
                run((s, c) => {
                  // Same reason as marking: after a parent undoes a
                  // validation the task is waiting again, and validating it a
                  // second time has to be a command the log has not seen.
                  const owner = s.tasks.get(taskId)?.memberId
                  const attempt =
                    owner === undefined ? 0 : taskDayOf(s, c.day, owner, taskId).attempt
                  return validateTask(
                    s,
                    { taskId, commandId: `${taskId}:${today}:val:${attempt}` as never },
                    c,
                  )
                }, actor),
              )
            }
            onReject={(taskId: TaskId, note: string) =>
              report(run((s, c) => returnTask(s, { taskId, note }, c), actor))
            }
            onUndo={(taskId: TaskId, reason: string) =>
              report(run((s, c) => revokeValidation(s, { taskId, reason }, c), actor))
            }
            onGrant={(purchaseId: PurchaseId) =>
              report(run((s, c) => grantPurchase(s, { purchaseId }, c), actor))
            }
            onDeliver={(purchaseId: PurchaseId) =>
              report(run((s, c) => deliverPurchase(s, { purchaseId }, c), actor))
            }
            onDeclareDay={(kind: DayKind) => {
              void run((s, c) => declareDayType(s, { day: c.day, kind }, c), actor)
            }}
            onPause={(memberId: string) => {
              void run(
                (s, c) =>
                  pauseDay(
                    s,
                    { day: c.day, memberId: memberId as MemberId, reason: 'Día en pausa' },
                    c,
                  ),
                actor,
              )
            }}
            onInsights={() => {
              setView({ name: 'insights' })
            }}
            onConfig={() => {
              setView({ name: 'config' })
            }}
            onShop={() => {
              setView({ name: 'shopEditor' })
            }}
            onPorQue={() => {
              setView({ name: 'porQue' })
            }}
            onCalendario={() => {
              setView({ name: 'calendario' })
            }}
            onSemaforo={() => {
              setView({ name: 'semaforoSetup' })
            }}
            onAssistant={
              assistantAvailable()
                ? () => {
                    setView({ name: 'assistant' })
                  }
                : undefined
            }
            onSystem={() => {
              setView({ name: 'system' })
            }}
            onExit={() => {
              // Se sale de la firma también: la próxima vez se vuelve a preguntar
              // quién es, que es el sentido de preguntarlo.
              setAdultoActual(null)
              setView({ name: 'who' })
            }}
          />
        )
      }

      case 'assistant': {
        if (!parentId) return null
        return (
          <Assistant
            session={session}
            state={state}
            actor={PARENT_ACTOR(parentId)}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )
      }

      case 'shopEditor': {
        if (!parentId) return null
        const actor = PARENT_ACTOR(parentId)
        const meeting = nextMeetingDay(state, today as never)
        return (
          <ShopEditor
            state={state}
            meetingDay={meeting}
            onCreate={(reward) =>
              report(
                run(
                  (s, c) => defineReward(s, { rewardId: c.newId() as RewardId, ...reward }, c),
                  actor,
                ),
              )
            }
            onReprice={(rewardId: RewardId, newPrice: number) =>
              report(
                run(
                  (s, c) => repriceReward(s, { rewardId, newPrice, effectiveFrom: meeting }, c),
                  actor,
                ),
              )
            }
            onArchive={(rewardId: RewardId, reason: string) =>
              report(run((s, c) => archiveReward(s, { rewardId, reason }, c), actor))
            }
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )
      }

      case 'semaforoSetup':
        return (
          <SemaforoSetup
            onStart={(minutes, limit) => {
              setView({ name: 'semaforo', minutes, limit })
            }}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )

      case 'semaforo':
        return (
          <Semaforo
            minutes={view.minutes}
            limit={view.limit}
            // Si alguno lo necesita, la barra no se anima para nadie: la
            // pantalla la miran los dos a la vez y no hay dos versiones.
            reducedMotion={children.some((c) => c.adaptations.reducedMotion)}
            onDone={() => {
              setView({ name: 'who' })
            }}
          />
        )

      case 'calendario':
        return (
          <Calendario
            events={session.events()}
            onImport={(rangos) => {
              if (!parentId) return
              // Un despacho por tramo: cada uno es una decisión distinta y el
              // registro tiene que poder decir cuál se aplicó y cuál no.
              for (const rango of rangos) {
                void run(
                  (s, c) =>
                    declareDayRange(
                      s,
                      {
                        from: rango.from as never,
                        to: rango.to as never,
                        kind: rango.kind,
                        note: rango.note,
                      },
                      c,
                    ),
                  PARENT_ACTOR(parentId),
                )
              }
            }}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )

      case 'porQue':
        return (
          <PorQue
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )

      case 'insights':
        return (
          <Insights
            state={state}
            events={session.events()}
            day={today}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )

      case 'config':
        return (
          <ChildConfig
            state={state}
            photos={photos}
            vault={vault}
            onPinChanged={() => {
              setPinsChanged((n) => n + 1)
            }}
            onAddAdult={(name: string) => {
              if (!parentId) return
              void run(
                (s, c) => addAdult(s, { memberId: c.newId() as MemberId, name }, c),
                PARENT_ACTOR(parentId),
              )
            }}
            onAddChild={(child) => {
              if (!parentId) return
              void run(
                (s, c) => addChild(s, { ...child, id: c.newId() as MemberId }, c),
                PARENT_ACTOR(parentId),
              )
            }}
            onArchiveMember={(memberId: MemberId, reason: string) => {
              if (!parentId) return
              void run((s, c) => archiveMember(s, { memberId, reason }, c), PARENT_ACTOR(parentId))
            }}
            onRestoreMember={(memberId: MemberId) => {
              if (!parentId) return
              void run((s, c) => restoreMember(s, { memberId }, c), PARENT_ACTOR(parentId))
            }}
            onRetune={(taskId: TaskId, coins: number, xp: number) => {
              if (!parentId) return
              void run(
                (s, c) =>
                  retuneTask(
                    s,
                    {
                      taskId,
                      // Las cuatro rejillas salen de la misma escalera que usa
                      // la semilla: un solo número que acordar, y la bajada por
                      // dominio ya viene puesta.
                      rewards: coins > 0 ? missionLadder(coins, xp) : routineLadder(xp),
                    },
                    c,
                  ),
                PARENT_ACTOR(parentId),
              )
            }}
            onReminders={(memberId: MemberId, avisos: readonly Reminder[]) => {
              if (!parentId) return
              void run((s, c) => setReminders(s, { memberId, avisos }, c), PARENT_ACTOR(parentId))
            }}
            onLook={(memberId: MemberId, accent: string, emoji: string, coinWord: string) => {
              if (!parentId) return
              void run(
                (s, c) => updateLook(s, { memberId, accent, emoji, coinWord }, c),
                PARENT_ACTOR(parentId),
              )
            }}
            onCreateTask={(memberId, task) =>
              report(
                run(
                  (s, c) => {
                    if (!parentId) return { ok: false, error: { code: 'not-allowed' } }
                    // The pay curve comes from the same file the seed uses, and a
                    // routine's is all zeroes. Typing a task by hand cannot invent
                    // one any more than describing it to the assistant can.
                    return defineTask(
                      s,
                      {
                        taskId: c.newId() as TaskId,
                        memberId,
                        kind: task.kind,
                        ...(task.moment !== undefined ? { moment: task.moment } : {}),
                        name: task.name,
                        steps: task.steps.map((text) => ({ id: c.newId() as StepId, text })),
                        difficulty: 2,
                        rewards:
                          task.kind === 'routine'
                            ? routineLadder(10)
                            : missionLadder(task.coins, 20),
                        promotionThreshold: 10,
                        requiresPhoto: false,
                        ...(task.game !== undefined ? { game: task.game } : {}),
                      },
                      c,
                    )
                  },
                  PARENT_ACTOR(parentId ?? ('' as MemberId)),
                ),
              )
            }
            onAdaptations={(memberId: MemberId, adaptations: Adaptations) => {
              if (!parentId) return
              void run(
                (s, c) => updateAdaptations(s, { memberId, adaptations }, c),
                PARENT_ACTOR(parentId),
              )
            }}
            onSetPhoto={(taskId: TaskId, photoId: PhotoId) => {
              if (!parentId) return
              void run(
                (s, c) => setReferencePhoto(s, { taskId, photoId }, c),
                PARENT_ACTOR(parentId),
              )
            }}
            onRename={(taskId: TaskId, name: string, emoji: string) => {
              if (!parentId) return
              void run((s, c) => renameTask(s, { taskId, name, emoji }, c), PARENT_ACTOR(parentId))
            }}
            onSteps={(taskId: TaskId, steps: readonly Step[]) => {
              if (!parentId) return
              void run((s, c) => updateSteps(s, { taskId, steps }, c), PARENT_ACTOR(parentId))
            }}
            onArchive={(taskId: TaskId, reason: string) => {
              if (!parentId) return
              void run((s, c) => archiveTask(s, { taskId, reason }, c), PARENT_ACTOR(parentId))
            }}
            onRestore={(taskId: TaskId) => {
              if (!parentId) return
              void run((s, c) => restoreTask(s, { taskId }, c), PARENT_ACTOR(parentId))
            }}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )

      case 'system': {
        // La unión de las horas de todos los hijos: la suscripción es del
        // aparato, así que tiene que cubrirlas todas.
        const todas = new Map<string, Reminder>()
        for (const child of children) {
          for (const aviso of child.reminders) {
            const previa = todas.get(aviso.hora)
            todas.set(aviso.hora, {
              hora: aviso.hora,
              previo: aviso.previo || (previa?.previo ?? false),
            })
          }
        }
        return (
          <SystemScreen
            avisos={[...todas.values()]}
            onBack={() => {
              setView({ name: 'parent' })
            }}
          />
        )
      }
    }
  }

  return (
    <>
      {banner}
      {body()}
    </>
  )
}
